import useGetMe from '@/api/queries/me/useGetMe';
import projectKeys from '@/api/queries/project/keyFactory';
import timelogKeys from '@/api/queries/timelog/keyFactor';
import { useGetTimelogTypes } from '@/api/queries/timelog/useGetTimelogTypes';
import DurationFieldset, {
  DURATION_SCHEMA,
} from '@/ui/pages/projectPage/components/LabourPanel/DurationFieldset';
import UserAndCostFieldset, {
  USER_AND_COST_SCHEMA,
} from '@/ui/pages/projectPage/components/LabourPanel/UserAndCostFieldset';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Form,
  Select,
  Sidebar,
  Textarea,
  SidebarProps as TSidebarProps,
} from '@payaca/ambrosia';

import {
  createTimelog,
  updateTimelog,
} from '@payaca/store/timelogs/timelogsActions';
import { TimelogLinkedEntityType } from '@payaca/types/timelogs';
import { Nullish } from '@payaca/utilities/types';
import { useQueryClient } from '@tanstack/react-query';
import { addHours, intervalToDuration, subHours } from 'date-fns';
import React, { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';

const CREATE_EDIT_TIMELOG_SCHEMA = z.object({
  duration: DURATION_SCHEMA,
  userAndCost: USER_AND_COST_SCHEMA,
  type: z.string().min(1, 'Type is required'),
  notes: z.string().optional(),
});

type CreateEditTimelogSchema = z.infer<typeof CREATE_EDIT_TIMELOG_SCHEMA>;

type PartialTimelog = {
  id: string;
  startTime: string;
  endTime?: Nullish<string>;
  costPerHour?: Nullish<{ value: number }>;
  notes?: Nullish<string>;
  user: { id: string };
  type?: Nullish<{ id: string }>;
};

export type TProps = Pick<TSidebarProps, 'isOpen' | 'onClose'> & {
  // Optional: present when recording/editing from a project's Labour tab.
  // Omitted on the account-wide reporting page, where the sidebar is used for
  // editing existing timelogs only (each timelog keeps its own linked entity).
  projectId?: string;
  timelog?: PartialTimelog;
  // Required to create a new timelog (which entity it's logged against);
  // not needed when editing, since an edit preserves the existing link.
  primaryLinkedEntity?: {
    entityType: TimelogLinkedEntityType;
    entityId: number;
  };
};

const getDefaultValuesFromTimelog = (
  timelog: PartialTimelog
): CreateEditTimelogSchema => {
  const startDate = new Date(timelog.startTime);
  const endDate = timelog.endTime
    ? new Date(timelog.endTime)
    : addHours(startDate, 1);

  const { days, minutes, hours } = intervalToDuration({
    start: startDate,
    end: endDate,
  });

  return {
    duration: {
      startDate,
      days: days || 0,
      hours: hours || 0,
      minutes: minutes || 0,
      endDate,
    },
    userAndCost: {
      userId: timelog.user.id,
      costPerHour: timelog.costPerHour?.value || 0,
    },
    type: timelog.type?.id || '',
    notes: timelog.notes || '',
  };
};

const getDefaultValues = (currentUser?: {
  id: string;
  costPerHour?: {
    value: number;
  } | null;
}) => {
  const now = new Date();
  const oneHourEarlier = subHours(now, 1);

  return {
    duration: {
      startDate: oneHourEarlier,
      days: 0,
      hours: 1,
      minutes: 0,
      endDate: now,
    },
    userAndCost: {
      userId: currentUser?.id || '',
      costPerHour: currentUser?.costPerHour?.value || 0,
    },
    type: '',
    notes: '',
  };
};

const CreateEditTimelogSidebar: FC<TProps> = (props) => {
  const { projectId, isOpen, onClose, primaryLinkedEntity, timelog } = props;

  const [isDirty, setIsDirty] = useState(false);

  const isEditing = !!timelog;

  return (
    <Sidebar
      title={isEditing ? 'Edit timelog' : 'Record timelog'}
      isOpen={isOpen}
      onClose={onClose}
      warnUserOnClose={isDirty}
    >
      <SidebarContent
        projectId={projectId}
        onClose={onClose}
        primaryLinkedEntity={primaryLinkedEntity}
        timelog={timelog}
        setIsDirty={setIsDirty}
      />
    </Sidebar>
  );
};

const SidebarContent: FC<
  Pick<TProps, 'projectId' | 'onClose' | 'primaryLinkedEntity' | 'timelog'> & {
    setIsDirty: (isDirty: boolean) => void;
  }
> = (props) => {
  const { projectId, onClose, primaryLinkedEntity, timelog, setIsDirty } =
    props;

  const isEditing = !!timelog;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { data } = useGetMe();
  const { timelogTypes } = useGetTimelogTypes();

  const formMethods = useForm<CreateEditTimelogSchema>({
    defaultValues: isEditing
      ? getDefaultValuesFromTimelog(timelog)
      : getDefaultValues(data?.user),
    resolver: zodResolver(CREATE_EDIT_TIMELOG_SCHEMA),
  });

  useEffect(() => {
    setIsDirty(formMethods.formState.isDirty);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'setIsDirty'
  }, [formMethods.formState.isDirty]);

  const handleSubmit = (data: CreateEditTimelogSchema) => {
    setErrorMessage(null);
    setIsProcessing(true);

    new Promise((resolve, reject) => {
      const timelogMutationObject = {
        startTime: data.duration.startDate,
        endTime: data.duration.endDate,
        assigneeType: 'user' as const,
        // warn: this key is expecting the numerical ID, not the public ID...
        assigneePublicId: data.userAndCost.userId,
        costPerHour: data.userAndCost.costPerHour,
        typePublicId: data.type,
        notes: data.notes?.length ? data.notes : undefined,
      };

      if (isEditing) {
        dispatch(
          updateTimelog.request({
            timelog: { publicId: timelog.id, ...timelogMutationObject },
            callback: (result) => resolve(result),
            onErrorCallback: (error) => reject(error),
          })
        );
      } else if (primaryLinkedEntity) {
        dispatch(
          createTimelog.request({
            timelog: {
              ...timelogMutationObject,
              primaryLinkedEntity: primaryLinkedEntity,
            },
            callback: (result) => resolve(result),
            onErrorCallback: (error) => reject(error),
          })
        );
      } else {
        reject(new Error('Cannot create a timelog without a linked entity.'));
      }
    })
      .then(() => {
        void queryClient.invalidateQueries({
          queryKey: timelogKeys.list(),
        });

        if (projectId) {
          void queryClient.invalidateQueries({
            queryKey: projectKeys.profitBreakdown(projectId),
          });
        }

        onClose?.();
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(
          `Failed to ${
            isEditing ? 'update' : 'record'
          } timelog. Please try again.`
        );
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <Form
      methods={formMethods}
      onSubmit={handleSubmit}
      className="flex flex-col h-full overflow-y-auto"
    >
      <Sidebar.Body className="p-4 space-y-4 overflow-visible">
        <Controller<CreateEditTimelogSchema, 'duration'>
          name="duration"
          render={({ field: { value, onChange } }) => {
            return <DurationFieldset duration={value} onChange={onChange} />;
          }}
        />

        {/* User and Cost per hour */}
        <Controller<CreateEditTimelogSchema, 'userAndCost'>
          name="userAndCost"
          render={({ field: { value, onChange } }) => {
            return (
              <UserAndCostFieldset userAndCost={value} onChange={onChange} />
            );
          }}
        />

        {/* Type */}
        <Controller<CreateEditTimelogSchema, 'type'>
          name="type"
          render={({ field: { name, value, onChange } }) => (
            <Field name={name}>
              <Field.Label>Type</Field.Label>
              <Select
                value={value}
                multiple={false}
                onChange={onChange}
                options={
                  timelogTypes.map((type) => ({
                    label: type.name,
                    value: type.id,
                  })) || []
                }
                placeholder="Select a type"
              />
            </Field>
          )}
        />

        {/* Notes */}
        <Controller<CreateEditTimelogSchema, 'notes'>
          name="notes"
          render={({ field: { name, value, onChange } }) => (
            <Field name={name}>
              <Field.Label>Notes</Field.Label>
              <Textarea value={value} onChange={onChange} />
            </Field>
          )}
        />
      </Sidebar.Body>
      <Sidebar.Footer className="space-y-4 sticky bottom-0">
        {errorMessage && <Alert colour="red">{errorMessage}</Alert>}

        <div className="flex gap-x-4 justify-stretch">
          <Button onClick={onClose} variant="secondary" className="w-full">
            Cancel
          </Button>
          <Button
            className="w-full"
            type="submit"
            disabled={isProcessing}
            isProcessing={isProcessing}
          >
            {isEditing ? 'Update' : 'Record'}
          </Button>
        </div>
      </Sidebar.Footer>
    </Form>
  );
};

export default CreateEditTimelogSidebar;
